import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Divider, Paper, Stack, Alert,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Add, Send } from '@mui/icons-material';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import api from '../../services/api';

type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

interface TicketUser {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

interface Ticket {
  _id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  category: string;
  priority: TicketPriority;
  status: TicketStatus;
  userId: TicketUser | null;
  assignedTo: { _id?: string; firstName: string; lastName: string } | null;
  resolution?: string;
  messages: Array<{ senderId: string; senderType: string; message: string; sentAt: string }>;
  createdAt: string;
}

interface Agent {
  _id: string;
  name: string;
}

const STATUS_COLOR: Record<TicketStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  open: 'info',
  in_progress: 'warning',
  waiting_customer: 'default',
  resolved: 'success',
  closed: 'error',
};

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
};

const ADMIN_SENDER_TYPES = new Set(['admin', 'staff', 'super_admin', 'client_admin', 'employee', 'tenant_admin']);

const EMPTY_CREATE = {
  phone: '',
  userId: '',
  customerName: '',
  subject: '',
  description: '',
  category: 'general',
  priority: 'medium' as TicketPriority,
  assignedTo: '',
};

export default function HelpdeskPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'complaint'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState<TicketStatus | ''>('');
  const [newPriority, setNewPriority] = useState<TicketPriority | ''>('');
  const [newAssignee, setNewAssignee] = useState('');
  const [resolution, setResolution] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [lookupError, setLookupError] = useState('');

  const { data: categories } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: async () => {
      const res = await api.post('/complaint/categories', { type: 'user' });
      return res.data.data as Array<{ name: string; complaint: string }>;
    },
  });

  const { data: agents } = useQuery({
    queryKey: ['helpdesk-agents'],
    queryFn: async () => {
      const res = await api.get('/helpdesk/agents');
      return res.data.data as Agent[];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['helpdesk-tickets', statusFilter, categoryFilter, sourceFilter, assigneeFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page + 1), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (sourceFilter === 'complaint') params.set('source', 'complaint');
      if (assigneeFilter) params.set('assignedTo', assigneeFilter);
      const res = await api.get(`/helpdesk/tickets?${params}`);
      return res.data.data;
    },
  });

  useEffect(() => {
    const ticketId = searchParams.get('ticketId');
    if (!ticketId) return;
    api.get(`/helpdesk/tickets/${ticketId}`).then((res) => {
      const ticket = res.data.data as Ticket;
      setSelectedTicket(ticket);
      setNewStatus(ticket.status);
      setNewPriority(ticket.priority);
      setNewAssignee(ticket.assignedTo?._id || '');
      setResolution(ticket.resolution || '');
    }).catch(() => {});
    const next = new URLSearchParams(searchParams);
    next.delete('ticketId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: stats } = useQuery({
    queryKey: ['helpdesk-stats'],
    queryFn: async () => {
      const res = await api.get('/helpdesk/stats');
      return res.data.data as Array<{ _id: TicketStatus; count: number }>;
    },
  });

  const refreshSelectedTicket = async (ticketId: string) => {
    const res = await api.get(`/helpdesk/tickets/${ticketId}`);
    const ticket = res.data.data as Ticket;
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setNewPriority(ticket.priority);
    setNewAssignee(ticket.assignedTo?._id || '');
    setResolution(ticket.resolution || '');
  };

  const replyMutation = useMutation({
    mutationFn: async ({ id, message }: { id: string; message: string }) => {
      await api.post(`/helpdesk/tickets/${id}/reply`, { message });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      setReplyText('');
      if (selectedTicket) await refreshSelectedTicket(selectedTicket._id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      id: string;
      status?: TicketStatus;
      priority?: TicketPriority;
      assignedTo?: string;
      resolution?: string;
    }) => {
      await api.put(`/helpdesk/tickets/${payload.id}`, {
        status: payload.status,
        priority: payload.priority,
        assignedTo: payload.assignedTo || undefined,
        resolution: payload.resolution,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['helpdesk-stats'] });
      if (selectedTicket) await refreshSelectedTicket(selectedTicket._id);
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      await api.post('/helpdesk/tickets/admin-create', {
        userId: createForm.userId,
        subject: createForm.subject,
        description: createForm.description,
        category: createForm.category,
        priority: createForm.priority,
        assignedTo: createForm.assignedTo || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['helpdesk-stats'] });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      setLookupError('');
    },
  });

  const lookupCustomer = async () => {
    setLookupError('');
    try {
      const res = await api.get('/admin/users/lookup', {
        params: { phone: createForm.phone, userType: 'customer' },
      });
      const user = res.data.data;
      if (!user?._id) {
        setLookupError('Customer not found for this phone number');
        setCreateForm((f) => ({ ...f, userId: '', customerName: '' }));
        return;
      }
      setCreateForm((f) => ({
        ...f,
        userId: user._id,
        customerName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.phone,
      }));
    } catch {
      setLookupError('Customer lookup failed');
    }
  };

  const openTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setNewPriority(ticket.priority);
    setNewAssignee(ticket.assignedTo?._id || '');
    setResolution(ticket.resolution || '');
    setReplyText('');
  };

  const columns: GridColDef[] = [
    { field: 'ticketNumber', headerName: 'Ticket #', width: 130 },
    { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
    {
      field: 'userId',
      headerName: 'Customer',
      width: 160,
      valueGetter: (_, row) => {
        const u = row.userId;
        return u ? `${u.firstName} ${u.lastName}` : '-';
      },
    },
    { field: 'category', headerName: 'Category', width: 110 },
    {
      field: 'assignedTo',
      headerName: 'Assigned',
      width: 130,
      valueGetter: (_, row) => {
        const a = row.assignedTo;
        return a ? `${a.firstName} ${a.lastName}`.trim() : 'Unassigned';
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={PRIORITY_COLOR[value] ?? 'default'} />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: ({ value }) => (
        <Chip label={value.replace('_', ' ')} size="small" color={STATUS_COLOR[value as TicketStatus] ?? 'default'} />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 140,
      valueFormatter: (value) => new Date(value as string).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" onClick={() => openTicket(row as Ticket)}>
          View
        </Button>
      ),
    },
  ];

  const totalTickets = data?.total ?? 0;

  return (
    <PageShell>
      <PageHeader
        title="Helpdesk"
        subtitle="Manage customer support tickets"
        actions={(
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create ticket
          </Button>
        )}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {(stats ?? []).map((s) => (
          <Paper key={s._id} sx={{ px: 2.5, py: 1.5, minWidth: 120, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700}>{s.count}</Typography>
            <Typography variant="caption" color="text.secondary">{s._id.replace('_', ' ')}</Typography>
          </Paper>
        ))}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          size="small"
          sx={{ width: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {(['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'] as TicketStatus[]).map((s) => (
            <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Source"
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value as 'all' | 'complaint'); setPage(0); }}
          size="small"
          sx={{ width: 180 }}
        >
          <MenuItem value="all">All tickets</MenuItem>
          <MenuItem value="complaint">Complaints only</MenuItem>
        </TextField>
        <TextField
          select
          label="Category"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          size="small"
          sx={{ width: 220 }}
        >
          <MenuItem value="">All categories</MenuItem>
          <MenuItem value="general">General</MenuItem>
          {(categories ?? []).map((c) => (
            <MenuItem key={c.name} value={c.name}>{c.complaint || c.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Assignee"
          value={assigneeFilter}
          onChange={(e) => { setAssigneeFilter(e.target.value); setPage(0); }}
          size="small"
          sx={{ width: 200 }}
        >
          <MenuItem value="">All agents</MenuItem>
          {(agents ?? []).map((a) => (
            <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      <SerialNoDataGrid
        rows={data?.tickets ?? []}
        columns={columns}
        loading={isLoading}
        rowCount={totalTickets}
        paginationMode="server"
        paginationModel={{ page, pageSize: 20 }}
        onPaginationModelChange={(m) => setPage(m.page)}
        getRowId={(r) => r._id}
        autoHeight
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create support ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Customer phone"
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                fullWidth
              />
              <Button variant="outlined" onClick={lookupCustomer} sx={{ mt: 1, whiteSpace: 'nowrap' }}>
                Find
              </Button>
            </Stack>
            {lookupError && <Alert severity="error">{lookupError}</Alert>}
            {createForm.customerName && (
              <Alert severity="success">Customer: {createForm.customerName}</Alert>
            )}
            <TextField
              label="Subject"
              value={createForm.subject}
              onChange={(e) => setCreateForm((f) => ({ ...f, subject: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              multiline
              rows={4}
              fullWidth
            />
            <TextField
              select
              label="Category"
              value={createForm.category}
              onChange={(e) => setCreateForm((f) => ({ ...f, category: e.target.value }))}
              fullWidth
            >
              <MenuItem value="general">General</MenuItem>
              {(categories ?? []).map((c) => (
                <MenuItem key={c.name} value={c.name}>{c.complaint || c.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Priority"
              value={createForm.priority}
              onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value as TicketPriority }))}
              fullWidth
            >
              {(['low', 'medium', 'high', 'urgent'] as TicketPriority[]).map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Assign to"
              value={createForm.assignedTo}
              onChange={(e) => setCreateForm((f) => ({ ...f, assignedTo: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">Unassigned</MenuItem>
              {(agents ?? []).map((a) => (
                <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createForm.userId || !createForm.subject.trim() || !createForm.description.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!selectedTicket} onClose={() => setSelectedTicket(null)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              #{selectedTicket.ticketNumber} — {selectedTicket.subject}
              <Chip
                label={selectedTicket.status.replace('_', ' ')}
                size="small"
                color={STATUS_COLOR[selectedTicket.status]}
                sx={{ ml: 2 }}
              />
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Customer:{' '}
                {selectedTicket.userId
                  ? `${selectedTicket.userId.firstName} ${selectedTicket.userId.lastName} · ${selectedTicket.userId.phone}`
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Assigned:{' '}
                {selectedTicket.assignedTo
                  ? `${selectedTicket.assignedTo.firstName} ${selectedTicket.assignedTo.lastName}`.trim()
                  : 'Unassigned'}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5} sx={{ maxHeight: 320, overflowY: 'auto', mb: 2 }}>
                {selectedTicket.messages.map((msg, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: ADMIN_SENDER_TYPES.has(msg.senderType) ? 'primary.50' : 'grey.50',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {msg.senderType} · {new Date(msg.sentAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" mt={0.5}>{msg.message}</Typography>
                  </Paper>
                ))}
              </Stack>

              <TextField
                label="Reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 2 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  select
                  label="Status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as TicketStatus)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  {(['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'] as TicketStatus[]).map((s) => (
                    <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Priority"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as TicketPriority)}
                  size="small"
                  sx={{ minWidth: 160 }}
                >
                  {(['low', 'medium', 'high', 'urgent'] as TicketPriority[]).map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Assign to"
                  value={newAssignee}
                  onChange={(e) => setNewAssignee(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {(agents ?? []).map((a) => (
                    <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                label="Resolution notes"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTicket(null)}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => updateMutation.mutate({
                  id: selectedTicket._id,
                  status: newStatus || undefined,
                  priority: newPriority || undefined,
                  assignedTo: newAssignee,
                  resolution,
                })}
                disabled={updateMutation.isPending}
              >
                Save changes
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => replyMutation.mutate({ id: selectedTicket._id, message: replyText })}
                disabled={!replyText.trim() || replyMutation.isPending}
              >
                Send Reply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PageShell>
  );
}
